import { NextPageWithLayout } from '@/common/common-types'
import PageLayout from '@/components/page-layout/page-layout'
import { GetStaticPropsContext } from 'next'
import { ReactElement } from 'react'
import { useTranslations } from 'next-intl'
import useSWR from 'swr'
import { useLocale } from 'next-intl'
import { withSEO } from '@/common/with-seo'
import retriveMessages, { LocaleType } from '@/messages/message'
import {
  getFaultReportingPageData,
  FaultReportingPageData,
} from '@/service/fault-reporting.service'
import { SEOData } from '@service/seo.service'
import clsx from 'clsx'
import PdMedia from '@/components/pd-media/pd-media'
import { ContactUsSection } from '@/components/contact-us-section'
import { WarrantyInfo } from '@/components/warranty-info'
import ServiceProgress from '@/components/service-progress/service-progress'

interface FaultReportingProps {
  initData: FaultReportingPageData
  seoData?: SEOData | null
}

const FaultReporting: NextPageWithLayout<FaultReportingProps> = ({
  initData,
  seoData,
}) => {
  const locale = useLocale()
  const t = useTranslations()

  const { data } = useSWR<FaultReportingPageData>(
    getFaultReportingPageData.swrkey(locale),
    () => getFaultReportingPageData(locale),
    { fallbackData: initData }
  )
  return (
    <div className="w-full h-fit bg-white">
      {/* 顶部 Banner 区域 - 背景图铺满，文字居中叠加 */}
      <div
        className={clsx(
          'w-full',
          'h-50 laptop:h-120',
          'bg-pdbg',
          'relative'
        )}
      >
        {/* 背景图 - 铺满整个 banner */}
        {data?.banner?.bg_image?.url && (
          <div className="absolute inset-0">
            <PdMedia
              src={data.banner.bg_image.url}
              alt={data.banner.title}
              mask={false}
              object="cover"
            />
          </div>
        )}
        {/* 文字内容 - 居中叠加在背景图上 */}
        <div
          className={clsx(
            'absolute inset-0',
            'flex items-center justify-center',
            'p-6 laptop:p-0'
          )}
        >
          <div
            className={clsx(
              'w-full laptop:w-[720px]',
              'flex flex-col',
              'gap-2',
              'items-start laptop:items-center',
              'text-left laptop:text-center'
            )}
          >
            <h1
              className={clsx(
                'w-full',
                'text-28 laptop:text-40',
                'font-semibold',
                'text-white',
                'leading-[34px] laptop:leading-[48px]'
              )}
            >
              {data?.banner?.title}
            </h1>
            <p
              className={clsx(
                'w-full',
                'text-14 laptop:text-16',
                'font-semibold laptop:font-normal',
                'text-white',
                'leading-[21px] laptop:leading-[24px]'
              )}
            >
              {data?.banner?.description}
            </p>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div
        className={clsx(
          'w-full',
          'pt-[60px] pb-[80px] laptop:pt-[120px] laptop:pb-[120px]',
        )}
      >
        <div
          className={clsx(
            'w-full max-w-300 mx-auto',
            'flex flex-col',
            'gap-12 laptop:gap-[120px]'
          )}
        >
          {/* 报修信息组件 */}
          <div className={clsx('w-full')}>
            <WarrantyInfo />
          </div>

          {/* 联系我们组件 */}
          {data?.contect_us && (
            <div className={clsx('w-full')}>
              <ContactUsSection
                data={data.contect_us}
              />
            </div>
          )}

          {/* 服务进度组件 */}
          <div className={clsx('w-full')}>
            <ServiceProgress />
          </div>
        </div>
      </div>
    </div>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['support', 'fault-reporting'], locale as LocaleType)
    const initData = await getFaultReportingPageData(locale as LocaleType)

    return {
      props: {
        messages,
        initData: initData || null,
        seoData: initData?.seoData,
      },
      revalidate: 60,
    }
  },
  () => '/support/fault-reporting'
)

FaultReporting.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype="website"
    >
      {page}
    </PageLayout>
  )
}

export default FaultReporting
